import createSpacing from '@mui/system/createTheme/createSpacing';
import { createTheme, getTheme, themes, densities } from '@weave-mui/styles';
/**
 * Loads the Weave MUI token set asynchronously and merges it with the MUI base
 * theme plus app-specific overrides (same tokens and component styles as pim-db-migration-admin).
 */
export async function loadAppTheme() {
    const muiBase = createTheme();
    const weaveTheme = await getTheme(themes.LIGHT_GRAY, densities.MEDIUM);
    return createTheme(muiBase, weaveTheme, {
        // Weave themes set `spacing` as a token array (fractional factors warn in MUI).
        // Weave's createTheme does not turn a bare `8` into a function — Toolbar etc.
        // need `theme.spacing` to be MUI's spacing helper, not a number.
        spacing: createSpacing(8),
        palette: {
            mode: 'light',
            primary: { main: '#0696D7' },
            secondary: { main: '#FF6B35' },
            error: { main: '#E53935' },
            warning: { main: '#F57C00' },
            success: { main: '#43A047' },
            info: { main: '#0288D1' },
            background: {
                default: '#f0f2f5',
                paper: '#ffffff',
            },
            divider: 'rgba(0,0,0,0.09)',
        },
        typography: {
            fontFamily: '"DM Sans", "Helvetica", "Arial", sans-serif',
            h4: { fontWeight: 700, fontFamily: '"Syne", sans-serif' },
            h5: { fontWeight: 700, fontFamily: '"Syne", sans-serif' },
            h6: { fontWeight: 700, fontFamily: '"Syne", sans-serif' },
            subtitle1: { fontWeight: 500 },
            subtitle2: { fontWeight: 600 },
        },
        shape: { borderRadius: 8 },
        components: {
            MuiTableCell: {
                styleOverrides: {
                    head: { fontWeight: 600, fontSize: '0.8125rem' },
                },
            },
            MuiAppBar: {
                defaultProps: { color: 'transparent', elevation: 0 },
                styleOverrides: {
                    root: {
                        backgroundColor: '#ffffff',
                        color: '#1a1a2e',
                        borderBottom: '1px solid rgba(0,0,0,0.09)',
                        backgroundImage: 'none',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: '1px solid rgba(0,0,0,0.09)',
                        boxShadow: 'none',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(0,0,0,0.02)',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        marginLeft: 8,
                        marginRight: 8,
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(6,150,215,0.1)',
                            color: '#0696D7',
                            '& .MuiListItemIcon-root': { color: '#0696D7' },
                            '&:hover': {
                                backgroundColor: 'rgba(6,150,215,0.15)',
                            },
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: { root: { fontWeight: 500 } },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        '&.MuiButton-outlinedPrimary': {
                            borderColor: '#0696D7',
                            color: '#0696D7',
                            '&:hover': {
                                borderColor: '#047aad',
                                color: '#047aad',
                                backgroundColor: 'rgba(6,150,215,0.04)',
                            },
                        },
                        '&.MuiButton-containedWarning': {
                            backgroundColor: '#F57C00',
                            '&:hover': { backgroundColor: '#E65100' },
                            '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.12)' },
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundImage: 'none',
                        border: '1px solid rgba(0,0,0,0.09)',
                    },
                },
            },
        },
    });
}
